/**
 * src/state/actions/useRegistryActions.js
 *
 * Asset and vendor registry action callbacks extracted from
 * useAppState.js as part of the file-splitting refactor. Each
 * useCallback body is moved verbatim — same statements, same
 * dependency arrays, same closure semantics.
 *
 * Sub-hook: useRegistryActions({ updateState, tenantId })
 *
 * Returns: saveAsset, deleteAsset, saveVendor, patchVendors, patchVendorsEach, patchVendor, archiveVendor,
 * restoreVendor, deleteVendor, saveProduct, deleteProduct
 */

import { useCallback } from 'react';
import { logActivity } from '../../api/activityLogger.js';
import { emitEngineEvent } from '../../engine/engineEventBus.js';
import * as vendorApi from '../../api/vendorApi.js';
import * as assetApi from '../../api/assetApi.js';
import {
  captureRecord,
  restoreRecord,
  beginRecordWrite,
  markRecordCommitted,
} from './optimisticRollback.js';
import { queueNetworkWrite } from '../networkRetryQueue.js';
import { diffVendor, overlayTheirs } from '../vendorPatch.js';

export function useRegistryActions({ updateState, tenantId, stateRef }) {
  // Persists through the dedicated assets-write per-record store (ADR-0005)
  // instead of the whole-tenant-state blob, so two users saving DIFFERENT
  // assets never falsely conflict (DB-CONC-002 — the storage-layer fix for
  // #4852's BIA analogue). Same optimistic-update-then-reconcile shape as
  // saveVendor below.
  const saveAsset = useCallback(
    async (asset, session) => {
      let isEdit = false;
      let before = null;
      // Taken before the optimistic insert so a rollback can tell whether a
      // sibling write for this same asset committed in the meantime — see
      // optimisticRollback.js's "Write fences" note.
      const fenceKey = `asset:${asset.id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        if (!s.assets) s.assets = [];
        const idx = s.assets.findIndex((a) => a.id === asset.id);
        isEdit = idx >= 0;
        before = captureRecord(s.assets, asset.id);
        if (isEdit) s.assets[idx] = asset;
        else s.assets = [asset, ...s.assets];
      });

      // Assets are stripped from the blob by state-write, so a failed
      // assets-write is never rescued by the debounced save's retry — the
      // optimistic record would linger in the list until the next reload and
      // then vanish (#4991). Put it back so the error the caller surfaces is
      // the whole story.
      const reconcile = (r) => {
        markRecordCommitted(fenceKey);
        if (!r?.asset) return;
        updateState((s) => {
          const idx = (s.assets || []).findIndex((a) => a.id === r.asset.id);
          if (idx >= 0) s.assets[idx] = r.asset;
          else s.assets = [r.asset, ...(s.assets || [])];
        });
      };
      let result;
      try {
        result = await assetApi.saveAssetRecord(asset, tenantId);
      } catch (e) {
        // Roll back unconditionally — #4991's contract is that the register
        // never shows a row the server has not stored, and that holds whether
        // the write was refused or never delivered.
        updateState((s) => {
          s.assets = restoreRecord(s.assets, asset.id, before, { fence });
        });
        // Then split on WHY it failed. A refusal is final: re-sending it
        // changes nothing, so the dialog's inline error is the whole story. A
        // write that never reached the server is the case usePersistence's
        // network-retry contract covers for the blob and that nothing covered
        // for the per-record stores — queue it, and let a landed retry
        // re-insert the record through the same reconcile path a first-time
        // success uses (#5242, #5253). The record reappears when it is
        // genuinely stored, never before.
        const queued = queueNetworkWrite({
          key: `asset:${asset.id}`,
          error: e,
          attempt: () => assetApi.saveAssetRecord(asset, tenantId),
          onSuccess: (r) => {
            reconcile(r || { asset });
            emitEngineEvent('asset.saved', { asset });
            if (session) {
              logActivity(tenantId, session, {
                module: 'Assets',
                actionType: isEdit ? 'edited' : 'created',
                recordId: asset.id,
                recordLabel: asset.name,
              });
            }
          },
        });
        // Lets the dialog phrase its inline error correctly: "not saved" vs.
        // "not saved yet, retrying". It must never say "saved" (STG-B-003).
        e.willRetry = queued;
        throw e;
      }
      reconcile(result);

      emitEngineEvent('asset.saved', { asset });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Assets',
          actionType: isEdit ? 'edited' : 'created',
          recordId: asset.id,
          recordLabel: asset.name,
        });
      }
    },
    [updateState, tenantId]
  );

  const deleteAsset = useCallback(
    async (id, label, session) => {
      let before = null;
      const fenceKey = `asset:${id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        before = captureRecord(s.assets, id);
        s.assets = (s.assets || []).filter((a) => a.id !== id);
      });

      // Same rollback contract as saveAsset above: a refused delete must not
      // leave the asset missing from the list. The engine is told only once the
      // delete has actually landed — announcing a deletion that was refused
      // would have downstream jobs act on a record that still exists.
      try {
        await assetApi.deleteAssetRecord(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s.assets = restoreRecord(s.assets, id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);

      emitEngineEvent('asset.deleted', { assetId: id });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Assets',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  const saveVendor = useCallback(
    async (vendor, session) => {
      // Vendor objects always carry a client-generated id (even brand-new
      // ones — see VendorFormModal), so presence of vendor.id cannot
      // distinguish create from edit; check against the current vendor list
      // instead, same as saveAsset above.
      //
      // Read synchronously off stateRef rather than inside the updateState
      // producer below: updateState's setState updater runs during React's
      // render pass, not necessarily at the moment this function calls it
      // (see usePersistence.js's "eager-state optimisation" note) — it only
      // runs synchronously when no other update is already pending on the
      // same fiber. When this save follows another updateState call in the
      // same tick (e.g. ProposeRiskFromVendorModal firing saveRisk() then
      // this saveVendor() one right after, without awaiting either), the
      // producer is deferred and `isEdit` would still read its stale initial
      // value here — sending an existing vendor down the create path, which
      // 409s ("Vendor already exists") and never reaches the update that
      // sets linkedRiskId (#5944). Same fix shape as saveRisk in
      // useRiskState.js.
      const isEdit = (stateRef?.current?.vendors || []).some((v) => v.id === vendor.id);
      let priorSubProcessors = null;
      let before = null;
      // See optimisticRollback.js's "Write fences" note: taken before the
      // optimistic update so a rollback cannot undo a sibling write that
      // committed while this one was in flight.
      const fenceKey = `vendor:${vendor.id}`;
      const fence = beginRecordWrite(fenceKey);

      // Optimistic local state update for immediate UI feedback.
      updateState((s) => {
        if (!s.vendors) s.vendors = [];
        const idx = s.vendors.findIndex((v) => v.id === vendor.id);
        before = captureRecord(s.vendors, vendor.id);
        if (idx >= 0) {
          priorSubProcessors = s.vendors[idx].subProcessors || [];
          s.vendors[idx] = vendor;
        } else {
          s.vendors = [vendor, ...s.vendors];
        }
      });

      // Persist to dedicated vendor store and reconcile (critical for new vendors
      // to receive server-assigned id, and for updatedAt to reflect the write
      // that actually landed — a stale local updatedAt would make the server's
      // optimistic-concurrency check on the NEXT edit reject a legitimate write
      // as a false conflict). vendors-write returns the single touched vendor
      // (result.vendor), never the whole array. Errors propagate to awaiting
      // callers (e.g. InvolvementWizardModal) so they can surface them.
      // Vendors are stripped from the blob by state-write once the tenant's
      // vendor store row exists, so a refused vendors-write is never rescued by
      // the debounced save — roll the record back rather than leave a row in
      // the register that the server never stored (same contract as saveAsset).
      let result;
      try {
        result = isEdit
          ? await vendorApi.updateVendor(vendor, tenantId)
          : await vendorApi.createVendor(vendor, tenantId);
      } catch (e) {
        updateState((s) => {
          s.vendors = restoreRecord(s.vendors, vendor.id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);
      if (result?.vendor) {
        updateState((s) => {
          const idx = (s.vendors || []).findIndex((v) => v.id === result.vendor.id);
          if (idx >= 0) s.vendors[idx] = result.vendor;
          else s.vendors = [result.vendor, ...(s.vendors || [])];
        });
      }

      // ── Engine events ──────────────────────────────────────
      emitEngineEvent('vendor.saved', { vendor });
      const newSubs = vendor.subProcessors || [];
      const subsChanged = !isEdit
        ? newSubs.length > 0
        : JSON.stringify(priorSubProcessors) !== JSON.stringify(newSubs);
      if (subsChanged) {
        emitEngineEvent('vendor.subprocessors_changed', {
          vendor,
          priorSubProcessors: priorSubProcessors || [],
          newSubProcessors: newSubs,
        });
      }

      if (session) {
        logActivity(tenantId, session, {
          module: 'Vendors',
          actionType: isEdit ? 'edited' : 'created',
          recordId: vendor.id,
          recordLabel: vendor.name,
        });
      }
    },
    [updateState, tenantId, stateRef]
  );

  // GH #7979 (TRYGGR_INTENT.md §13 c/d): field-level vendor write. Only the
  // patched fields are sent, each with the value it held when the user loaded
  // it (`expected[id][field]`), so a concurrent edit of another field of the
  // same vendor lands beside this one instead of being overwritten or
  // refused. A field someone else changed meanwhile comes back as a 409 with
  // `error.conflicts` (azureClient) and nothing of this request is stored.
  //
  // Local state: the patched fields are applied optimistically (fenced as in
  // saveVendor) and the server's rows are adopted on success. On a refused
  // write the record is put back; on a clash it is then shown with what is
  // stored now at each clashing field, and the error is rethrown so the
  // surface can offer Keep mine / Use theirs with the user's edit intact.
  //
  // `patches` is keyed by vendor id, so each vendor may carry its own subset
  // of an edit; the request is still one all-or-nothing write. `shared`, when
  // given, is the one patch every vendor carries (the `patch` request form).
  const writeVendorPatches = useCallback(
    async (patches, expected, session, shared) => {
      const ids = Object.keys(patches || {}).filter(
        (id) => patches[id] && Object.keys(patches[id]).length
      );
      if (!ids.length) return [];
      const fences = new Map(ids.map((id) => [id, beginRecordWrite(`vendor:${id}`)]));
      const befores = new Map();
      updateState((s) => {
        for (const id of ids) {
          befores.set(id, captureRecord(s.vendors, id));
          const idx = (s.vendors || []).findIndex((v) => v.id === id);
          if (idx >= 0) s.vendors[idx] = { ...s.vendors[idx], ...patches[id] };
        }
      });

      let result;
      try {
        result = shared
          ? await vendorApi.bulkUpdateVendors(ids, shared, expected, tenantId)
          : await vendorApi.bulkPatchVendors(
              Object.fromEntries(ids.map((id) => [id, patches[id]])),
              expected,
              tenantId
            );
      } catch (e) {
        updateState((s) => {
          for (const id of ids) {
            s.vendors = restoreRecord(s.vendors, id, befores.get(id), { fence: fences.get(id) });
          }
          if (Array.isArray(e?.conflicts)) s.vendors = overlayTheirs(s.vendors, e.conflicts);
        });
        throw e;
      }
      for (const id of ids) markRecordCommitted(`vendor:${id}`);
      const saved = Array.isArray(result?.vendors) ? result.vendors : [];
      if (saved.length) {
        updateState((s) => {
          const byId = new Map(saved.map((v) => [v.id, v]));
          s.vendors = (s.vendors || []).map((v) => byId.get(v.id) || v);
        });
      }

      for (const vendor of saved) {
        const prior = befores.get(vendor.id)?.record;
        emitEngineEvent('vendor.saved', { vendor });
        if (
          Object.prototype.hasOwnProperty.call(patches[vendor.id] || {}, 'subProcessors') &&
          JSON.stringify(prior?.subProcessors || []) !== JSON.stringify(vendor.subProcessors || [])
        ) {
          emitEngineEvent('vendor.subprocessors_changed', {
            vendor,
            priorSubProcessors: prior?.subProcessors || [],
            newSubProcessors: vendor.subProcessors || [],
          });
        }
        if (session) {
          logActivity(tenantId, session, {
            module: 'Vendors',
            actionType: 'edited',
            recordId: vendor.id,
            recordLabel: vendor.name,
          });
        }
      }
      return saved;
    },
    [updateState, tenantId]
  );

  // One patch applied to every listed vendor.
  const patchVendors = useCallback(
    async (vendorIds, patch, expected, session) => {
      const ids = [...new Set(vendorIds || [])];
      if (!ids.length || !patch || !Object.keys(patch).length) return [];
      const patches = Object.fromEntries(ids.map((id) => [id, patch]));
      return writeVendorPatches(patches, expected, session, patch);
    },
    [writeVendorPatches]
  );

  // A patch per vendor, as one request (a bulk edit's clash answer).
  const patchVendorsEach = useCallback(
    (patches, expected, session) => writeVendorPatches(patches, expected, session, null),
    [writeVendorPatches]
  );

  // The vendor edit form's save: send only what the user changed since the
  // form opened (`base`), never the whole record.
  const patchVendor = useCallback(
    async (base, edited, session) => {
      const { patch, expected } = diffVendor(base, edited);
      if (!Object.keys(patch).length) return null;
      const saved = await patchVendors([edited.id], patch, { [edited.id]: expected }, session);
      return saved[0] || null;
    },
    [patchVendors]
  );

  // Archive (soft delete) — set archivedAt so the record and any attached
  // compliance data survive for audit; the register filters it out. Used for
  // vendors that carry attached data (VDR-B-004). Never touches the linked
  // risk (VDR-B-001) — only the vendors array is mutated.
  const archiveVendor = useCallback(
    async (id, label, session) => {
      const ts = new Date().toISOString();
      let before = null;
      const fenceKey = `vendor:${id}`;
      const fence = beginRecordWrite(fenceKey);
      // Optimistic local update
      updateState((s) => {
        const idx = (s.vendors || []).findIndex((v) => v.id === id);
        before = captureRecord(s.vendors, id);
        if (idx >= 0) {
          s.vendors[idx].archivedAt = ts;
          s.vendors[idx].archivedBy = session?.userId || null;
          s.vendors[idx].lastUpdated = ts;
        }
      });

      let result;
      try {
        result = await vendorApi.archiveVendor(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s.vendors = restoreRecord(s.vendors, id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);
      if (result?.vendor)
        updateState((s) => {
          const idx = (s.vendors || []).findIndex((v) => v.id === result.vendor.id);
          if (idx >= 0) s.vendors[idx] = result.vendor;
        });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Vendors',
          actionType: 'archived',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  // Restore an archived vendor back into the active register.
  const restoreVendor = useCallback(
    async (id, label, session) => {
      const ts = new Date().toISOString();
      let before = null;
      const fenceKey = `vendor:${id}`;
      const fence = beginRecordWrite(fenceKey);
      // Optimistic local update
      updateState((s) => {
        const idx = (s.vendors || []).findIndex((v) => v.id === id);
        before = captureRecord(s.vendors, id);
        if (idx >= 0) {
          delete s.vendors[idx].archivedAt;
          delete s.vendors[idx].archivedBy;
          s.vendors[idx].lastUpdated = ts;
        }
      });

      let result;
      try {
        result = await vendorApi.restoreVendor(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s.vendors = restoreRecord(s.vendors, id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);
      if (result?.vendor)
        updateState((s) => {
          const idx = (s.vendors || []).findIndex((v) => v.id === result.vendor.id);
          if (idx >= 0) s.vendors[idx] = result.vendor;
        });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Vendors',
          actionType: 'restored',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  // Hard delete — permanently removes the vendor record. Only callers that
  // have verified the vendor carries no attached data should use this
  // (VDR-B-004). Does not cascade to linked risks/ROPA/assets (VDR-B-001).
  const deleteVendor = useCallback(
    async (id, label, session) => {
      let before = null;
      const fenceKey = `vendor:${id}`;
      const fence = beginRecordWrite(fenceKey);
      // Optimistic local update
      updateState((s) => {
        before = captureRecord(s.vendors, id);
        s.vendors = (s.vendors || []).filter((v) => v.id !== id);
      });

      // Engine notified only once the delete has landed — see deleteAsset.
      let result;
      try {
        result = await vendorApi.deleteVendor(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s.vendors = restoreRecord(s.vendors, id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);

      emitEngineEvent('vendor.deleted', { vendorId: id });

      if (result?.vendors)
        updateState((s) => {
          s.vendors = result.vendors;
        });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Vendors',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  // ── Products (PDE registry — CRA module) ─────────────────────────────────
  // Products live in state.products[] per tenant (PROD-B-001). CRUD persists
  // through the standard updateState → debounced state-write path; there is no
  // separate product CRUD Azure Function.
  const saveProduct = useCallback(
    (product, session) => {
      let isEdit = false;
      let priorStatus = null;
      updateState((s) => {
        if (!s.products) s.products = [];
        const idx = s.products.findIndex((p) => p.id === product.id);
        isEdit = idx >= 0;
        if (isEdit) {
          priorStatus = s.products[idx].lifecycleStatus;
          s.products[idx] = product;
        } else {
          s.products = [product, ...s.products];
        }
      });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Products',
          actionType: isEdit ? 'edited' : 'created',
          recordId: product.id,
          recordLabel: product.name,
        });

        // CRA Art. 13(19) end-of-life notification stub — when a product
        // transitions into 'discontinued', record an activity-log row. The
        // actual notifier (Timer Trigger) is adjacent work (separate issue).
        if (product.lifecycleStatus === 'discontinued' && priorStatus !== 'discontinued') {
          logActivity(tenantId, session, {
            module: 'Products',
            actionType: 'discontinued',
            recordId: product.id,
            recordLabel: `${product.name} — end-of-life notice (CRA Art. 13(19))`,
          });
        }
      }
    },
    [updateState, tenantId]
  );

  // Soft delete — the entry is retained with a deletedAt timestamp so the
  // audit trail and any anchored evidence survive; the list view filters it out.
  const deleteProduct = useCallback(
    (id, label, session) => {
      const ts = new Date().toISOString();
      updateState((s) => {
        const idx = (s.products || []).findIndex((p) => p.id === id);
        if (idx >= 0) {
          s.products[idx].deletedAt = ts;
          s.products[idx].updatedAt = ts;
        }
      });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Products',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  return {
    saveAsset,
    deleteAsset,
    saveVendor,
    patchVendors,
    patchVendorsEach,
    patchVendor,
    archiveVendor,
    restoreVendor,
    deleteVendor,
    saveProduct,
    deleteProduct,
  };
}
